import type { CardId } from './cards.js';
import { legalMoves } from './rules.js';
import { SEATS, type Match, type Move, type Phase, type Seat, type Trick } from './types.js';

/**
 * Bir oyuncunun görebileceği durum.
 *
 * Sunucudan istemciye SADECE bu gider. Tam `Match` nesnesi asla ağa çıkmaz —
 * rakiplerin elleri orada duruyor. Tip ayrımı bilerek keskin: yanlışlıkla
 * `Match` göndermek derleme hatası versin.
 */
export interface PlayerView {
  seat: Seat;
  phase: Phase;
  hand: CardId[];
  /** Her koltuğun elindeki kart SAYISI — kartların kendisi değil. */
  handCounts: number[];
  trick: Trick;
  turn: Seat;
  finishOrder: Seat[];
  scores: number[];
  dealIndex: number;
  set: number;
  /** Rütbe başına oynanmış kart sayısı (index 0 = rütbe 3). "Kaç tane 2 kaldı?" için. */
  playedByRank: number[];
  /** Sıra sendeyse dolu; UI'daki kart vurgulama bunun üzerinden çalışır. */
  legalMoves: Move[];
  exchange: ExchangeView | null;
  /**
   * Tamamlanan her elin bitiş sırası, eskiden yeniye. Puan tablosu bunu
   * satır satır gösteriyor; puanlar sıradan türüyor (`PLACE_POINTS`).
   */
  finishOrders: Seat[][];
  /**
   * Bu elin başındaki değişim BİTTİKTEN sonra ne alıp ne verdiğin.
   * Değişim fazı boyunca null — karşı tarafın seçimi o sırada henüz gizli.
   * El boyunca dolu kalır; istemci ne kadar göstereceğine kendisi karar verir.
   */
  lastExchange: ExchangeSummary | null;
}

export interface ExchangeView {
  /** Zorunlu olarak sana geçen kartlar (kaybedenin en yüksekleri). */
  received: CardId[];
  /** Zorunlu olarak senden alınan kartlar. */
  lost: CardId[];
  /** Senden beklenen serbest seçim; yoksa null. */
  mustGive: { to: Seat; count: number } | null;
  /** Seçimini yaptın mı? */
  submitted: boolean;
  /** Hangi koltuklar hâlâ bekleniyor (herkes bunu görebilir — kart içeriği değil). */
  waitingOn: Seat[];
}

/**
 * Değişim bittikten sonra iki tarafın da gördüğü özet.
 *
 * Zorunlu yön ve serbest yön birlikte veriliyor: kaybeden, kazananın hangi
 * kartları seçtiğini ancak burada öğreniyor. Yalnızca değişime KATILAN iki
 * koltuk için dolu — diğer çiftin kartları görünmüyor.
 */
export interface ExchangeSummary {
  /** Değişimi yaptığın oyuncu. */
  partner: Seat;
  /** Eline geçen kartlar. */
  received: CardId[];
  /** Elinden çıkan kartlar. */
  given: CardId[];
}

export function publicView(match: Match, seat: Seat): PlayerView {
  const deal = match.deal;
  const empty: Trick = { count: 0, rank: null, holder: null, passStreak: 0, plays: [] };

  return {
    seat,
    phase: match.phase,
    hand: deal ? [...deal.hands[seat]!] : [],
    handCounts: SEATS.map((s) => (deal ? deal.hands[s]!.length : 0)),
    trick: deal ? deal.trick : empty,
    turn: deal ? deal.turn : seat,
    finishOrder: deal ? [...deal.finishOrder] : [],
    scores: [...match.scores],
    dealIndex: match.dealIndex,
    set: Math.floor(match.dealIndex / match.config.dealsPerSet) + 1,
    playedByRank: deal ? [...deal.playedByRank] : new Array(13).fill(0),
    legalMoves:
      deal && match.phase === 'play' && deal.turn === seat ? legalMoves(deal, seat) : [],
    exchange: exchangeView(match, seat),
    finishOrders: match.finishOrders.map((o) => [...o]),
    lastExchange: exchangeSummary(match, seat),
  };
}

function exchangeSummary(match: Match, seat: Seat): ExchangeSummary | null {
  const ex = match.exchange;
  // Faz sürerken null: karşı tarafın seçimi henüz açılmamalı.
  if (!ex || match.phase === 'exchange') return null;

  const mine = [...ex.forced, ...ex.chosen].filter((t) => t.from === seat || t.to === seat);
  if (mine.length === 0) return null;

  const head = mine[0]!;
  return {
    partner: head.from === seat ? head.to : head.from,
    received: mine.filter((t) => t.to === seat).flatMap((t) => t.cards),
    given: mine.filter((t) => t.from === seat).flatMap((t) => t.cards),
  };
}

function exchangeView(match: Match, seat: Seat): ExchangeView | null {
  const ex = match.exchange;
  if (!ex || match.phase !== 'exchange') return null;

  const pending = ex.pending.find((p) => p.from === seat) ?? null;
  const submittedSeats = new Set(ex.chosen.map((c) => c.from));

  /**
   * Zorunlu VE seçilmiş transferlerin ikisi de sayılıyor.
   *
   * Önce yalnızca `forced` bakılıyordu. Sonucu şuydu: kartı ZORUNLU VEREN
   * taraf (3. ve 4.) karşılığında ne aldığını hiç görmüyordu — ona gelen
   * kartlar karşı tarafın seçimi olduğu için `chosen` içinde duruyor. Aynı
   * şekilde serbest seçim yapan (1. ve 2.) verdiği kartları göremiyordu.
   *
   * Gizlilik korunuyor: yalnızca BU koltuğa gelen ya da bu koltuktan giden
   * transferler süzülüyor, başkalarının kendi arasındaki değişim görünmüyor.
   */
  const mine = [...ex.forced, ...ex.chosen];

  return {
    received: mine.filter((t) => t.to === seat).flatMap((t) => t.cards),
    lost: mine.filter((t) => t.from === seat).flatMap((t) => t.cards),
    mustGive: pending ? { to: pending.to, count: pending.count } : null,
    submitted: submittedSeats.has(seat),
    waitingOn: ex.pending.filter((p) => !submittedSeats.has(p.from)).map((p) => p.from),
  };
}
